// Imports ------------------------------------------------------------------->

import { Component } from '../Component'
import { element } from '../CustomElement'

import { inject } from '../../services/di'
import { AuthenticationService } from '../../services/AuthenticationService'
import { ConfigService } from '../../services/ConfigService'
import { icons } from '../../icons/Icons'
import { i18n } from '../../i18n/I18n'
import { Link } from '../flipp-link/Link'
import { LinkService } from '../../services/LinkService'
import { SocialIconsContainer } from './SocialIconsContainer'
import { FormInput } from '../flipp-form-input/FormInput'
import {
    handleFormInputsChange,
    handleSubmitRequest,
    hasEmptyFields,
    setDocumentTitle,
} from './Login.helper'
import { WarningToast } from './WarningToast'

@element('flipp-signup-page')
export class SignupPage extends Component<any, void> {
    @inject config: ConfigService
    @inject auth: AuthenticationService

    refs: {
        submitForm: HTMLFormElement
        submitBtn: HTMLElement
        signinBtn: HTMLElement
        spinner: HTMLElement
        emailInput: FormInput
        passwordInput: FormInput
        confirmPasswordInput: FormInput
        submitBtnText: HTMLElement
        passwordPolicy: HTMLElement
        responseErrorMsg: HTMLElement
        termsCheckbox: HTMLElement
        privacyCheckbox: HTMLElement
        toast: WarningToast
        socialIcons: SocialIconsContainer
    }

    state = {
        email: '',
        password: '',
        confirmPassword: '',
        terms: false,
        privacy: false,
    }

    onCreate() {
        setDocumentTitle(i18n('signup_header'))
    }

    async onAttach() {
        this.state.email = ''
        this.state.password = ''
        this.state.confirmPassword = ''
        this.state.terms = false
        this.state.privacy = false
        const res = await this.auth.getPasswordRequirements()
        this.refs.passwordPolicy.textContent = res.password_requirements
        this.refs.termsCheckbox.addEventListener(
            'input',
            this.handleCheckboxChanged
        )
        this.refs.privacyCheckbox.addEventListener(
            'input',
            this.handleCheckboxChanged
        )
    }

    onDetach() {
        this.refs.termsCheckbox.removeEventListener(
            'input',
            this.handleCheckboxChanged
        )
        this.refs.privacyCheckbox.removeEventListener(
            'input',
            this.handleCheckboxChanged
        )
    }

    handleCheckboxChanged = (e: any) => {
        Object.assign(this.state, { [e.target.name]: e.target.checked })

        if (this.state.terms && this.state.privacy) {
            this.refs.toast.classList.remove('show')
            this.refs.socialIcons.setAttribute('isDisabled', 'false')
        } else {
            this.refs.socialIcons.setAttribute('isDisabled', 'true')
        }
    }

    render() {
        return (
            <main>
                <div class="top-bar">
                    <Link to="listing" class="flipp-logo" title="Flipp">
                        {icons.flippLogoWhite}
                    </Link>
                </div>
                <div class="content">
                    <div>
                        <WarningToast ref="toast" />
                    </div>
                    <div class="form-container">
                        <div class="header">{i18n('signup_header')}</div>
                        <div class="account-text">
                            {i18n('signup_have_account')}
                            <Link href="/signin">{i18n('signin')}</Link>.
                        </div>
                        <form
                            name="submitForm"
                            class="form"
                            ref="submitForm"
                            onSubmit={this.handleSubmit}
                        >
                            <FormInput
                                ref="emailInput"
                                name={'email'}
                                handleChange={this.handleChange}
                                type={'email'}
                                placeholder={i18n('email')}
                            />
                            <FormInput
                                ref="passwordInput"
                                name={'password'}
                                handleChange={this.handleChange}
                                type={'password'}
                                placeholder={i18n('password')}
                            />
                            <FormInput
                                ref="confirmPasswordInput"
                                name={'confirmPassword'}
                                handleChange={this.handleChange}
                                type={'password'}
                                placeholder={i18n('confirm_password')}
                            />
                            <div ref="passwordPolicy" class="password-policy" />
                            <div class="checkbox-wrapper">
                                <input
                                    id="terms"
                                    name="terms"
                                    ref="termsCheckbox"
                                    type="checkbox"
                                />
                                <label for="terms">
                                    {i18n('accept_terms')}
                                    <a href={i18n('terms_url')} target="_blank">
                                        {i18n('terms_of_use')}
                                    </a>
                                </label>
                            </div>
                            <div class="checkbox-wrapper">
                                <input
                                    id="privacy"
                                    name="privacy"
                                    ref="privacyCheckbox"
                                    type="checkbox"
                                />
                                <label for="privacy">
                                    {i18n('accept_privacy')}
                                    <a
                                        href={i18n('privacy_url')}
                                        target="_blank"
                                    >
                                        {i18n('privacy_policy')}
                                    </a>
                                </label>
                            </div>
                            <div class="button-container">
                                <button ref="submitBtn" class="flipp-button">
                                    <div
                                        ref="spinner"
                                        class="spinner hide"
                                    ></div>
                                    <div
                                        ref="submitBtnText"
                                        class="submit-button-text"
                                    >
                                        <span>{i18n('signup')}</span>
                                    </div>
                                </button>
                                <div
                                    ref="underButton"
                                    class="under-button-error"
                                >
                                    <p
                                        ref="responseErrorMsg"
                                        class="error-msg"
                                    ></p>
                                    <div
                                        ref="signinBtn"
                                        class="under-button-text hide"
                                    >
                                        <span class="under-button-action">
                                            {i18n('please')}
                                            <Link href="/signin">
                                                {i18n('signin_action')}
                                            </Link>
                                            .
                                        </span>
                                    </div>
                                </div>
                                <div class="divider">
                                    <div class="line" />
                                    <div class="text">{i18n('or')}</div>
                                    <div class="line" />
                                </div>
                                <SocialIconsContainer
                                    ref="socialIcons"
                                    isDisabled={'true'}
                                    buttonClick={this.onSocialButtonClick}
                                />
                            </div>
                        </form>
                    </div>
                    <div class="picture">
                        <img
                            class="signup-image"
                            src={`https://f.wishabi.net/creative/Distribution/images/login-picture.svg`}
                            alt=""
                        />
                    </div>
                </div>
            </main>
        )
    }

    private signup = async () => {
        await this.auth.register({
            email: this.state.email,
            password: this.state.password,
        })
        const uri = `confirm_email?email=${encodeURIComponent(this.state.email)}`
        LinkService.getInstance().execute(uri, this)
    }

    private handleChange = (e: any) => {
        this.state = handleFormInputsChange(this.state, this.refs, e)
        this.refs.responseErrorMsg.classList.remove('with-action')
    }

    private onSocialButtonClick = () => {
        if (!this.state.terms || !this.state.privacy) {
            this.refs.toast.classList.add('show')
        }
    }

    private handleSubmit = async (e: any) => {
        e.preventDefault()
        this.refs.responseErrorMsg.textContent = ''

        if (
            e.submitter.className.trim() === 'google' ||
            e.submitter.className.trim() === 'facebook'
        ) {
            return
        }

        if (!this.state.terms || !this.state.privacy) {
            this.refs.toast.classList.add('show')
            return
        }

        this.refs.toast.classList.remove('show')

        if (hasEmptyFields(this.state)) {
            if (this.state.email.length === 0) {
                this.refs.emailInput.errorMessage = i18n('enter_email_error')
            }
            if (this.state.password.length === 0) {
                this.refs.passwordInput.errorMessage = i18n(
                    'enter_password_error'
                )
            }
            if (this.state.confirmPassword.length === 0) {
                this.refs.confirmPasswordInput.errorMessage = i18n(
                    'confirm_password_error'
                )
            }
        }
        if (
            this.state.password &&
            this.state.confirmPassword &&
            this.state.password !== this.state.confirmPassword
        ) {
            this.refs.confirmPasswordInput.errorMessage = i18n(
                'passwords_mismatch_error'
            )
        } else if (!hasEmptyFields(this.state)) {
            await handleSubmitRequest(this.refs, this.signup, (body) => {
                if (body.code === 'EMAIL_EXISTS') {
                    this.refs.signinBtn.classList.remove('hide')
                    this.refs.responseErrorMsg.classList.add('with-action')
                    this.refs.responseErrorMsg.textContent =
                        i18n('EMAIL_EXISTS')
                }
            })
        }
    }
}
